import * as React from "react";
import { CopyToClipboardButton } from "components/CopyToClipboardButton/CopyToClipboardButton";
import { LabelValue } from "components/LabelValue/LabelValue";
import { ConfirmationDialog } from "components/ConfirmationDialog/ConfirmationDialog";
import DialogContentText from "@mui/material/DialogContentText";
import { useDialogState } from "hooks/useDialogState";
import { ClientConfig, ClientsService } from "gen/clients";
import { useMutation } from "react-query";
import { Stack } from "@mui/material";
import { ApiErrorMessage } from "components/ApiErrorMessage/ApiErrorMessage";
import Button from "@mui/material/Button";

interface Props {
  clientConfig: ClientConfig;
  disabled?: boolean;
  onSaveSuccess: () => void;
}

const SignUpUrlField: React.FC<Props> = ({ clientConfig, disabled, onSaveSuccess }) => {
  const [signUpToken, setSignUpToken] = React.useState<string | undefined>(clientConfig.signUpToken);
  const [isDeleteSignUpConfirmationDialogOpen, openDeleteSignUpConfirmationDialog, closeSignUpConfirmationDialog] =
    useDialogState();

  const {
    mutate,
    error: mutateError,
    isLoading
  } = useMutation("updateClientConfig", {
    onSuccess: onSaveSuccess,
    mutationFn: ClientsService.updateClientConfig
  });

  const onGenerateSignUpUrlClick = React.useCallback(() => {
    let token = "";
    for (let i = 0; i < 8; i++) {
      token += Math.floor(Math.random() * 10);
    }
    setSignUpToken(token);
    mutate({
      id: clientConfig.id,
      requestBody: {
        ...clientConfig,
        signUpToken: token
      }
    });
  }, [setSignUpToken, clientConfig, mutate]);

  const onDeleteSignUpUrlConfirmation = React.useCallback(() => {
    setSignUpToken("");
    mutate({
      id: clientConfig.id,
      requestBody: {
        ...clientConfig,
        signUpToken: undefined
      }
    });
  }, [setSignUpToken, clientConfig, mutate]);

  return (
    <Stack spacing={1}>
      <LabelValue
        label="Sign-Up URL"
        value={
          <>
            <div>
              {signUpToken && (
                <span>
                  {`${import.meta.env.VITE_PUBLIC_PORTAL_URL}/signUp/${encodeURIComponent(
                    btoa(clientConfig.id)
                  )}/${signUpToken}`}
                </span>
              )}
              {!signUpToken && (
                <Button
                  disabled={disabled || isLoading}
                  variant="outlined"
                  size="small"
                  onClick={onGenerateSignUpUrlClick}
                >
                  Generate
                </Button>
              )}
              {signUpToken && (
                <>
                  <CopyToClipboardButton
                    variant="outlined"
                    size="small"
                    sx={{ ml: 1 }}
                    textToCopy={`${import.meta.env.VITE_PUBLIC_PORTAL_URL}/signUp/${encodeURIComponent(
                      btoa(clientConfig.id)
                    )}/${signUpToken}`}
                  >
                    Copy
                  </CopyToClipboardButton>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={openDeleteSignUpConfirmationDialog}
                    sx={{ ml: 1 }}
                    disabled={disabled || isLoading}
                  >
                    Delete
                  </Button>
                </>
              )}
            </div>
          </>
        }
      />
      {mutateError && <ApiErrorMessage apiError={mutateError} />}
      {isDeleteSignUpConfirmationDialogOpen && (
        <ConfirmationDialog
          title="Delete Sign-Up URL?"
          content={
            <DialogContentText>
              Are you sure you want to delete Sign-Up URL? This will make the custom Sign-Up page unavailable to users
              who bookmarked it.
            </DialogContentText>
          }
          onClose={closeSignUpConfirmationDialog}
          onConfirm={() => {
            closeSignUpConfirmationDialog();
            onDeleteSignUpUrlConfirmation();
          }}
          confirmationButtonColor="error"
        />
      )}
    </Stack>
  );
};

export { SignUpUrlField };
